import React, { useEffect, useState } from "react";
import { Rocket, Loader2, Sparkles, Copy, Check, Zap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { launchCampaign, getAutoLaunch, setAutoLaunch } from "../../api";
import { useToast } from "../../hooks/use-toast";

const STAGE_ICON = { Teaser: "👀", "Guess the Price": "💷", "Launch Reel": "🎬", Carousel: "🖼️", "Area Guide": "📍" };

const StepCard = ({ step, draft }) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(draft?.content || ""); setCopied(true); setTimeout(() => setCopied(false), 1300); } catch { toast({ title: "Couldn't copy" }); } };
  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-5" data-testid="launch-step">
      <div className="flex items-center justify-between mb-3">
        <span className="inline-flex items-center gap-2 text-[14px] font-500 text-[#2b2b2b]"><span className="text-[18px]">{STAGE_ICON[step.stage] || "✨"}</span> {step.stage}</span>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-[#8a8a8a]">{step.timing} · {step.format} · {step.platform}</span>
          {draft?.content && <button onClick={copy} className="inline-flex items-center gap-1 text-[12px] text-[#8a8a8a] hover:text-[#b8935f] transition-colors" data-testid="launch-copy">{copied ? <Check size={13} /> : <Copy size={13} />}</button>}
        </div>
      </div>
      {draft?.image && step.format === "Carousel" && (draft.images || []).length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2">{(draft.images || []).map((img, i) => <img key={i} src={img} alt="" className="h-16 w-16 object-cover rounded-lg border border-[#f0ece5] shrink-0" />)}</div>
      )}
      <p className="text-[13px] text-[#3a3a3a] whitespace-pre-wrap leading-relaxed">{draft?.content || "—"}</p>
    </div>
  );
};

const LaunchCampaigns = ({ properties = [], onSaved }) => {
  const { toast } = useToast();
  const [propertyId, setPropertyId] = useState("none");
  const [tone, setTone] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [auto, setAuto] = useState(false);
  const [autoLoaded, setAutoLoaded] = useState(false);

  useEffect(() => { getAutoLaunch().then((r) => setAuto(!!r.enabled)).catch(() => {}).finally(() => setAutoLoaded(true)); }, []);

  const toggleAuto = async (val) => {
    setAuto(val);
    try { await setAutoLaunch({ enabled: val }); toast({ title: val ? "Auto-launch on" : "Auto-launch off", description: val ? "New listings will auto-generate a full launch sequence." : undefined }); }
    catch { setAuto(!val); toast({ title: "Couldn't update setting" }); }
  };

  const run = async () => {
    if (propertyId === "none") { toast({ title: "Pick a listing to launch" }); return; }
    setBusy(true); setResult(null);
    try {
      const r = await launchCampaign(propertyId, { tone });
      setResult(r);
      toast({ title: `Launch sequence created (${r.count} steps)`, description: "Saved to Pending Review." });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: "Couldn't create launch", description: e?.response?.data?.detail || "Please try again." });
    } finally { setBusy(false); }
  };

  const draftFor = (draftId) => (result?.created || []).find((d) => d.id === draftId);

  return (
    <div data-testid="launch-campaigns">
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6 mb-6">
        <h3 className="font-display text-[22px] font-400 text-[#2b2b2b] mb-1 flex items-center gap-2"><Rocket size={20} className="text-[#b8935f]" /> Property Launch Campaigns</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-5">One click builds a full launch sequence — Teaser → Guess the Price → Launch Reel → Carousel → Area Guide — all saved to Pending Review.</p>

        <div className="flex items-center justify-between gap-3 bg-[#faf7f2] border border-[#f0ece5] rounded-xl p-4 mb-5">
          <div>
            <p className="text-[14px] font-500 text-[#2b2b2b] flex items-center gap-1.5"><Zap size={15} className="text-[#b8935f]" /> Auto-launch new listings</p>
            <p className="text-[12px] text-[#8a8a8a]">When on, every new listing automatically gets its own launch sequence drafted.</p>
          </div>
          {autoLoaded && <Switch checked={auto} onCheckedChange={toggleAuto} data-testid="auto-launch-switch" />}
        </div>

        <div className="grid sm:grid-cols-[1fr_200px_auto] gap-3">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="h-[46px] border-[#e2d8c8]" data-testid="launch-property"><SelectValue placeholder="Pick a listing to launch" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Pick a listing…</SelectItem>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger className="h-[46px] border-[#e2d8c8]" data-testid="launch-tone"><SelectValue placeholder="Tone" /></SelectTrigger>
            <SelectContent>{["Warm & friendly", "Professional", "Luxury", "Playful", "Urgent"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={run} disabled={busy} className="inline-flex items-center justify-center gap-2 px-6 h-[46px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60" data-testid="launch-create">
            {busy ? <><Loader2 size={16} className="animate-spin" /> Building…</> : <><Sparkles size={16} /> Create launch</>}
          </button>
        </div>
      </div>

      {busy && (
        <div className="rounded-2xl border border-[#f0ece5] bg-white flex flex-col items-center justify-center text-center p-10">
          <Loader2 size={28} className="text-[#b8935f] animate-spin mb-3" />
          <p className="text-[14px] text-[#6a6a6a]">Crafting your 5-step launch sequence…</p>
        </div>
      )}
      {result && (
        <div className="space-y-3" data-testid="launch-result">
          <p className="text-[13px] text-[#8a8a8a]">Launch sequence for <strong className="text-[#2b2b2b]">{result.property?.title}</strong> — find all steps in Pending Review to approve & publish.</p>
          {(result.steps || []).map((s) => <StepCard key={s.draftId} step={s} draft={draftFor(s.draftId)} />)}
        </div>
      )}
    </div>
  );
};

export default LaunchCampaigns;
